import {
    all, put, PutEffect, select, SelectEffect, takeLatest,
} from "@redux-saga/core/effects";
import axios, {AxiosResponse} from "axios";
import {Nullable, safeSaga, transformRequestOptions} from "@data-force/app";
import {
    CLAIM_JOB_PHASE,
    COMPLETE_JOB_PHASE,
    GET_JOB_LIST,
    GET_JOB_PHASE_WORKFLOW_DEFINITIONS,
    GET_PHASE_CONFIG_LIST,
} from "../actions/availableActions";
import {
    ClaimJobPhaseAction,
    CompleteJobPhaseAction,
    GetJobListAction,
    GetJobPhaseWorkflowDefinitionsAction,
    OpenJobEditorAction,
    SetActiveJobTypeAction,
    SetJobListAction,
    SetJobListCursorAction,
    SetJobPhaseWorkflowDefinitionsAction,
    SetPhaseConfigListAction,
    SetSelectedJobAction,
    ShowJobNotificationAction,
    UpdateJobListAction,
} from "../../interfaces/store/actions/job";
import {getActiveJobFilter, getBaseUrl} from "./main";
import StoreState from "../../interfaces/store/storeState";
import {
    getJobList,
    getJobPhaseWorkflowDefinitions,
    openJobEditor,
    setActiveJobType,
    setJobList,
    setJobListCursor,
    setJobPhaseWorkflowDefinitions,
    setPhaseConfigList,
    setSelectedJob,
    showJobNotification,
    updateJobList,
} from "../actions/job";
import {WorkflowDefinition} from "../../interfaces/store/actions/payload/workflowDefinition";
import {JobEntity, PhaseConfig, PhaseConfigNormalized} from "../../interfaces/store/actions/payload/job";
import {DisplayErrorMessageAction} from "../../interfaces/store/actions/main";
import {ClaimJobPhaseResponse, GetJobListPhaseStatsResponse, GetJobPhaseListResponse} from "../../interfaces/store/sagas/job";
import {
    JobTag, JobType, JobNotification, JobFilterType,
} from "../../enums/enums";
import {TaskPhaseStats} from "../../interfaces/store/actions/payload/task";
import {setLoading} from "../actions/mask";
import {SetLoadingAction} from "../../interfaces/store/actions/mask";

type CompleteJobPhaseHandlerIteratorType =
    SelectEffect
    | Promise<AxiosResponse>
    | PutEffect<GetJobListAction | ShowJobNotificationAction>;

function* completeJobPhaseHandler(
    {
        payload: {
            projectId, jobId, event, phase, phaseName, notificationType,
        },
    }: CompleteJobPhaseAction,
): IterableIterator<CompleteJobPhaseHandlerIteratorType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();

    const axiosResponse: Nullable<AxiosResponse> = yield axios.post(`${baseUrl}/project/${projectId}/job/${jobId}/complete`, {
        event,
        phase,
    });

    if (axiosResponse) {
        const {status} = axiosResponse;
        yield put(showJobNotification({
            phaseName: `${phase} - ${phaseName}`,
            type: status === 200 ? notificationType as string : JobNotification.ERROR,
        }));
    }

    const title: Nullable<string> = yield select((state: StoreState): Nullable<string> => state.job.searchValue);
    const filter: Nullable<string> = yield getActiveJobFilter();
    yield put(getJobList({
        title,
        filter,
    }));
}

type GetJobPhaseWDType =
    SelectEffect
    | Promise<AxiosResponse<WorkflowDefinition[]>>
    | PutEffect<SetJobPhaseWorkflowDefinitionsAction | SetJobListAction>;

function* getJobPhaseWDHandler(): IterableIterator<GetJobPhaseWDType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();
    const axiosResponse: Nullable<AxiosResponse<WorkflowDefinition[]>> = yield axios.get(`${baseUrl}/workflow-definition`);

    if (axiosResponse) {
        const {data}: AxiosResponse<WorkflowDefinition[]> = axiosResponse;
        yield put(setJobPhaseWorkflowDefinitions(data));
    }
}

type GetJobListHandlerIteratorType =
    SelectEffect
    | Promise<AxiosResponse<GetJobListPhaseStatsResponse | GetJobPhaseListResponse>>
    | PutEffect<GetJobPhaseWorkflowDefinitionsAction
    | SetLoadingAction
    | SetJobListAction
    | SetJobListCursorAction
    | UpdateJobListAction>;

function* getJobListHandler({payload}: GetJobListAction): IterableIterator<GetJobListHandlerIteratorType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();
    yield put(setLoading(true));
    const activeJobType: Nullable<string> = yield select(
        (state: StoreState): Nullable<string> => state.job.activeJobType,
    );

    const jobPhaseResponse: Nullable<AxiosResponse<GetJobPhaseListResponse>> = yield axios.get(`${baseUrl}/job/phase`, {
        params: {
            ...payload?.cursor,
            sortDirection: "DESC",
            title: payload?.title,
            filter: payload?.filter,
            tags: activeJobType === JobType.FEATURED ? [JobTag.FEATURED] : [],
        },
        paramsSerializer: (params) => transformRequestOptions(params),
    });

    if (jobPhaseResponse) {
        const {data}: AxiosResponse<GetJobPhaseListResponse> = jobPhaseResponse;
        let jobList: JobEntity[] = data?.items || [];
        jobList = jobList.map((job: JobEntity): JobEntity => {
            const {phaseStats}: JobEntity = job;
            let completedTaskCount: number = 0;

            if (phaseStats) {
                const {
                    COMPLETED = 0,
                    REJECTED = 0,
                }: TaskPhaseStats = phaseStats;
                completedTaskCount = COMPLETED + REJECTED;
            }

            return {
                ...job,
                completedTaskCount,
            };
        });

        const workflowDefinitionsLength: Nullable<number> = yield select(
            (state: StoreState): Nullable<number> => state.job.workflowDefinitions.length,
        );

        if (!workflowDefinitionsLength) {
            yield put(getJobPhaseWorkflowDefinitions());
        }

        if (payload?.updateList) {
            yield put(updateJobList(jobList));
        } else {
            yield put(setJobList(jobList));
        }

        if (data.cursor) {
            yield put(setJobListCursor(data.cursor));
        }
    }

    yield put(setLoading(false));
}

type ClaimJobPhaseIteratorType =
    SelectEffect
    | Promise<AxiosResponse>
    | PutEffect<GetJobListAction
    | SetActiveJobTypeAction
    | DisplayErrorMessageAction
    | OpenJobEditorAction
    | SetSelectedJobAction>;

function* claimJobPhaseHandler({
    payload: {
        projectId, jobId, phaseName, openEditor,
    },
}: ClaimJobPhaseAction): IterableIterator<ClaimJobPhaseIteratorType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();

    try {
        const claimJobPhaseResponse: Nullable<AxiosResponse<ClaimJobPhaseResponse>> = yield axios.post(
            `${baseUrl}/project/${projectId}/job/${jobId}/claim`,
            {phaseName},
        );
        if (claimJobPhaseResponse) {
            const {data}: AxiosResponse<ClaimJobPhaseResponse> = claimJobPhaseResponse;
            yield put(setActiveJobType(JobType.IN_PROGRESS));
            yield put(getJobList({
                filter: JobFilterType.ONGOING,
            }));

            const axiosResponse: Nullable<AxiosResponse<JobEntity>> = yield axios.get(
                `${baseUrl}/project/${data.projectId}/job/${data.jobId}/phase/${data.jobPhaseId}`,
            );
            if (axiosResponse) {
                const {data: jobData}: AxiosResponse<JobEntity> = axiosResponse;
                yield put(openJobEditor(openEditor));
                yield put(setSelectedJob(jobData));
            }
        }
    } catch (e) {
        yield put(setActiveJobType(JobType.AVAILABLE));
        yield put(getJobList({
            filter: JobFilterType.CLAIMABLE,
        }));
    }
}

type GetPhaseConfigListHandlerIteratorType =
    SelectEffect
    | Promise<AxiosResponse<PhaseConfig[]>>
    | PutEffect<SetPhaseConfigListAction>;

function* getPhaseConfigListHandler(): IterableIterator<GetPhaseConfigListHandlerIteratorType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();
    const axiosResponse: Nullable<AxiosResponse<PhaseConfig[]>> = yield axios.get(`${baseUrl}/phase-config`);
    if (axiosResponse) {
        const {data}: AxiosResponse<PhaseConfig[]> = axiosResponse;
        const normalizedData: PhaseConfigNormalized = {};
        data.forEach((phaseConfig: PhaseConfig): void => {
            normalizedData[`${phaseConfig.projectType}-${phaseConfig.name}`] = phaseConfig;
        });
        yield put(setPhaseConfigList(normalizedData));
    }
}

export function* jobSagaActionWatcher() {
    yield all([
        takeLatest(COMPLETE_JOB_PHASE, safeSaga(completeJobPhaseHandler)),
        takeLatest(GET_JOB_PHASE_WORKFLOW_DEFINITIONS, safeSaga(getJobPhaseWDHandler)),
        takeLatest(GET_JOB_LIST, safeSaga(getJobListHandler)),
        takeLatest(CLAIM_JOB_PHASE, safeSaga(claimJobPhaseHandler)),
        takeLatest(GET_PHASE_CONFIG_LIST, safeSaga(getPhaseConfigListHandler)),
    ]);
}
